import type {
  AiInsightClientOption,
  AiInsightClientRequest,
  AiInsightResponse,
  AiIncomePolicyBand,
  BankConfig,
  FinancingResult,
  LeadInput,
  LeadRecord,
  SimulationInput
} from "../../shared/contracts";
import { calculationFingerprint } from "../../shared/finance-facts";

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers
    }
  });
  const body = (await response.json().catch(() => ({}))) as { error?: string } & T;
  if (!response.ok) throw new Error(body.error ?? "Não foi possível concluir a operação.");
  return body;
}

export async function getBanks(): Promise<BankConfig[]> {
  const result = await request<{ banks: BankConfig[] }>("/api/banks");
  return result.banks;
}

export async function sendLead(input: LeadInput): Promise<void> {
  await request<{ ok: boolean }>("/api/leads", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export function incomePolicyBand(
  minimumIncome: number,
  monthlyIncome: number,
  overPolicyLimit?: boolean
): AiIncomePolicyBand {
  const pressureToPolicyLimit = minimumIncome / monthlyIncome;
  if (overPolicyLimit ?? pressureToPolicyLimit > 1 + 1e-9) {
    return "over-policy-limit";
  }
  if (pressureToPolicyLimit >= 0.8 - 1e-9) return "near-policy-limit";
  return "below-attention-threshold";
}

function toAiInsightClientOption(
  input: SimulationInput,
  result: FinancingResult
): AiInsightClientOption {
  return {
    bankId: result.bankId,
    system: result.system,
    calculationFingerprint: calculationFingerprint(result),
    eligibilityReasonCodes: result.eligibility.reasonCodes,
    incomePolicyBand: incomePolicyBand(
      result.eligibility.minimumIncome,
      input.monthlyIncome,
      result.eligibility.reasonCodes.includes("income-commitment")
    )
  };
}

export function buildAiInsightRequest(
  input: SimulationInput,
  selected: FinancingResult,
  results: FinancingResult[]
): AiInsightClientRequest {
  const alternatives = results
    .filter((result) => result.key !== selected.key)
    .slice(0, 7);
  return {
    scenario: {
      propertyValue: input.propertyValue,
      downPayment: input.downPayment,
      termMonths: input.termMonths
    },
    selected: toAiInsightClientOption(input, selected),
    alternatives: alternatives.map((result) => toAiInsightClientOption(input, result))
  };
}

export async function getAiInsight(
  input: SimulationInput,
  selected: FinancingResult,
  results: FinancingResult[]
): Promise<AiInsightResponse> {
  return request<AiInsightResponse>("/api/ai/insight", {
    method: "POST",
    body: JSON.stringify(buildAiInsightRequest(input, selected, results))
  });
}

export async function getAdminSession(): Promise<boolean> {
  const result = await request<{ authenticated: boolean }>("/api/admin/session");
  return result.authenticated;
}

export async function adminLogin(token: string): Promise<void> {
  await request<{ ok: boolean }>("/api/admin/login", {
    method: "POST",
    body: JSON.stringify({ token })
  });
}

export async function adminLogout(): Promise<void> {
  await request<{ ok: boolean }>("/api/admin/logout", { method: "POST" });
}

export async function getAdminBanks(): Promise<BankConfig[]> {
  const result = await request<{ banks: BankConfig[] }>("/api/admin/banks");
  return result.banks;
}

export async function saveAdminBanks(banks: BankConfig[]): Promise<BankConfig[]> {
  const result = await request<{ banks: BankConfig[] }>("/api/admin/banks", {
    method: "PUT",
    body: JSON.stringify({ banks })
  });
  return result.banks;
}

export async function getAdminLeads(): Promise<LeadRecord[]> {
  const result = await request<{ leads: LeadRecord[] }>("/api/admin/leads");
  return result.leads;
}
